import { IconButton } from '@creditornot/cb-button';
import { MoreHorizontal, Pencil, Trashbin } from '@creditornot/cb-icons';
import { MenuActionItem } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import ErrorToastContent from 'common/components/ErrorToastContent';
import { deleteOptionBinding } from 'modules/shared-menu/api';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';

import { useSelectionData } from '../../../../hooks/useSelectionData';
import { Offer, Option } from '../../types';

type MinimalOfferActionsProps = {
  offer: Offer;
  activeOption: Option;
};

const StyledPopover = styled(Popover).attrs({
  placement: 'bottom-start',
  renderArrow: false,
})`
  display: flex;
  align-items: center;
`;

const ToggleButton = styled(IconButton).attrs({ variant: 'blue', size: 'tiny' })``;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 200px;
`;

export const MinimalOfferActions = ({ offer, activeOption }: MinimalOfferActionsProps) => {
  const { updateSelectionData } = useSelectionData();
  const { selection } = useSelectionInfoContext();
  const { menuEditorPage } = useListingManagerRoutes();

  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);

  const offerName = offer.name[0].value;
  const optionName = activeOption.name[0].value;

  const optionBindingId = offer.option_bindings?.find(ob => ob.option_id === activeOption.id)?.id;

  const { mutate: executeDeleteOptionBinding } = useMutation(
    () =>
      optionBindingId
        ? deleteOptionBinding(selection.id, offer.id, optionBindingId)
        : Promise.reject('Option binding not found'),
    {
      onSuccess: updatedOffer => {
        updateSelectionData({ actionType: 'updateOffer', payload: updatedOffer });
        toast.success(
          <FormattedMessage
            id="shared-menu.editor.notifications.create.option.delete-binding"
            values={{
              offerName,
              optionName,
              bold: (text: string) => <b>{text}</b>,
            }}
          />,
        );
      },
      onError: error => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  return (
    <div onClick={e => e.stopPropagation()}>
      <StyledPopover
        content={
          <PopoverContent>
            <MenuActionItem
              variant="black"
              role="link"
              onClick={() => {
                window.open(`${menuEditorPage()}?item=${offer.id}`, '_blank');
              }}
              icon={<Pencil />}
            >
              <FormattedMessage id="shared-menu.editor.option-panel.button-edit-item" />
            </MenuActionItem>
            <MenuActionItem variant="red" onClick={() => executeDeleteOptionBinding()} icon={<Trashbin />}>
              <FormattedMessage id="shared-menu.editor.option-panel.button-remove-item" />
            </MenuActionItem>
          </PopoverContent>
        }
        onClickOutside={handleClose}
        onClickInside={handleClose}
        show={show}
      >
        <ToggleButton
          tabIndex={-1}
          onClick={e => {
            e.preventDefault();
            setShow(!show);
          }}
        >
          <MoreHorizontal />
        </ToggleButton>
      </StyledPopover>
    </div>
  );
};
